import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Box, Typography, IconButton, CircularProgress, Tooltip, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next';
import { Error, Replay } from '@mui/icons-material';
import { useCheckShippingCompanyAcceptOrderMutation } from 'src/_api/mapitShipApi';
import { findLast } from 'lodash';

export default function MapitShipOrderStatus({ row, value }) {
    const { t } = useTranslation();
    const theme = useTheme();

    const shipment = row?.otoIntegration?.returnShipment ?? row?.otoIntegration?.shipment;
    const [timeLeft, setTimeLeft] = useState(null);

    const [checkShippingCompanyAcceptOrder, { isLoading }] = useCheckShippingCompanyAcceptOrderMutation()

    const checkShippingCompanyAcceptOrderHandler = async () => {
        try {
            await checkShippingCompanyAcceptOrder(row?.id).unwrap();
        } catch (error) { }
    }

    const getFailedReason = (row) => {
        const failedStatus = findLast(row?.statusTrack, { status: "ORDER_FAILED" });
        return failedStatus?.errorMessage || '';
    }

    useEffect(() => {
        if (row?.shipmentUpdate?.date) {
            const updateDate = moment(row.shipmentUpdate.date);
            const diff = 300 - moment().diff(updateDate, 'seconds');

            if (diff > 0) {
                setTimeLeft(diff);
                const interval = setInterval(() => {
                    setTimeLeft(prev => {
                        if (prev <= 1) {
                            clearInterval(interval);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
                return () => clearInterval(interval);
            }
        }
    }, [row?.shipmentUpdate?.date]);

    const formatTimeLeft = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    if (row?.orderType === "SATCHELS_DELIVERY" && row?.currentStatus === "ORDER_PENDING") {
        return (
            <Tooltip
                placement="top"
                title={t(`mapit-ship-status.ORDER_CANCELED`)}
            >
                <Typography>
                    {t(`mapit-ship-status.ORDER_CANCELED`)}
                </Typography>
            </Tooltip>
        )
    }

    if (
        //===For Aramex=====
        (
            value !== "ORDER_RETURN" &&
            !!shipment?.mainTrackingNumber &&
            (shipment?.mainTrackingNumber !== shipment?.trackingNumber)
        )
        ||
        //===For Redox=====
        (
            shipment?.currentStatus === "returnShipmentProcessing" &&
            shipment?.deliveryOption?.deliveryOptionName === "Redbox"
        )
    ) {
        return (
            <Tooltip title={t("Return Processing")} placement="top">
                <Typography variant='body2'>
                    {t("Return Processing")}
                </Typography>
            </Tooltip>
        );
    } else if (shipment?.currentStatus === "pending" && ["ORDER_ASSIGNED_TO_SHIPPING_COMPANY", "ORDER_COMPLETED"]?.includes(value)) {
        return (
            <Box display="flex" alignItems="center" gap={2}>
                <Tooltip title={t("Waiting for shipping company confirmation")} placement="top">
                    <Typography
                        variant="subtitle1"
                        sx={{
                            maxWidth: theme.direction === "rtl" ? "88%" : "45%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {t("Waiting for shipping company confirmation")}
                    </Typography>
                </Tooltip>
                {
                    row?.shipmentUpdate?.count < 2 &&
                    <>
                        {timeLeft > 0 ? (
                            <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                {formatTimeLeft(timeLeft)}
                            </Typography>
                        ) : (

                            isLoading ?
                                <CircularProgress size={20} />
                                :
                                <IconButton onClick={(event) => {
                                    event.stopPropagation();
                                    checkShippingCompanyAcceptOrderHandler(row?.id)
                                }} color='primary'>
                                    <Replay sx={{ fontSize: 20 }} />
                                </IconButton>
                        )}
                    </>
                }
            </Box>
        );
    } else if (value === "ORDER_CANCELED") {
        return <Tooltip
            placement="top"
            title={t(`mapit-ship-status.${value}`)}
        >
            <Typography>
                {t(`mapit-ship-status.${value}`)}
            </Typography>
        </Tooltip>
    } else if (shipment?.currentStatus === "shipmentCanceled" && ["ORDER_PENDING", "ORDER_COMPLETED"]?.includes(value)) {
        return (
            <Tooltip
                placement="top"
                title={t("Shipment Canceled")}
            >
                <Typography variant='body2'>
                    {t("Shipment Canceled")}
                </Typography>
            </Tooltip>)
    } else if (shipment?.currentStatus === "failed" && ["ORDER_PENDING", "ORDER_COMPLETED", "ORDER_CANCELED"]?.includes(value)) {
        return (
            <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title={getFailedReason(row)} placement="top">
                    <Error color="error" />
                </Tooltip>
                <Tooltip title={t("Shipment creation failed") + (row?.otoIntegration?.returnShipment ? ` - (${t("returned")})` : "")} placement="top">
                    <Typography variant='body2'>
                        {t("Shipment creation failed") + (row?.otoIntegration?.returnShipment ? ` - (${t("returned")})` : "")}
                    </Typography>
                </Tooltip>
            </Box>
        )
    } else {
        return (
            <Tooltip
                placement="top"
                title={!!value ? `${t(`mapit-ship-status.${value}`)} ${!!row?.otoIntegration?.returnShipment ? ` - (${t("returned")})` : ""}` : ''}
            >
                <Typography variant='body2'>
                    {!!value ? `${t(`mapit-ship-status.${value}`)} ${!!row?.otoIntegration?.returnShipment ? ` - (${t("returned")})` : ""}` : ''}
                </Typography>
            </Tooltip>
        )
    }
}